import { components, operations, paths } from '@chanzuckerberg/argus-api';
import createClient from 'openapi-fetch';
import { ConfigApi } from '@backstage/core-plugin-api';
import { OktaTokenApi } from '@eng-portal/czi-extensions-react';
import { LoggerService } from '@backstage/backend-plugin-api';
import { promises as fs } from 'fs';

export type ArgusApp = components['schemas']['App'];
export type ArgusEnv = components['schemas']['Env'];
export type ArgusCluster = components['schemas']['Cluster'];
export type ArgusStack = components['schemas']['Stack'];
export type ArgusStackInfo = components['schemas']['StackInfo'];
export type ArgusStackEnriched = {
  stack: ArgusStack
  info?: ArgusStackInfo
};
export type ArgusStackDependency = components['schemas']['StackDependency'];
export type ArgusStats = components['schemas']['Stats'];
export type ArgusEvent = components['schemas']['Event'];
export type EventQuery = NonNullable<operations['listEvents']['parameters']['query']>;

interface TokenProvider {
  getToken(): Promise<string>
}

export interface ArgusAPI {
  getStackInfo(appName: string, envName: string, stackName: string): Promise<ArgusStackInfo | undefined>;
  getStats(): Promise<ArgusStats>;

  listApps(): Promise<ArgusApp[]>;
  listEnvs(appName: string): Promise<ArgusEnv[]>;
  listStacks(appName: string, envName: string): Promise<ArgusStack[]>;
  listClusters(): Promise<ArgusCluster[]>;
  listEvents(query: EventQuery): Promise<{ events: ArgusEvent[], hasMore: boolean, totalEventCount: number }>;
  listEventTypes(): Promise<string[]>;
  listSourceSystems(): Promise<string[]>;

  deleteStack(appName: string, envName: string, stackName: string): Promise<void>;
  reportDependencies(appName: string): Promise<ArgusStackDependency[]>;
}

export class ArgusClient implements ArgusAPI {
  private readonly client: ReturnType<typeof createClient<paths>>;
  private readonly logger?: LoggerService;
  private readonly tokenProvider: TokenProvider;

  constructor(configApi: ConfigApi, tokenProvider: TokenProvider, logger?: LoggerService) {
    const config = configApi.getConfig('plugins.argus');
    const baseUrl = config.getString('baseUrl');
    this.client = createClient<paths>({ baseUrl });
    this.logger = logger;
    this.tokenProvider = tokenProvider;
  }

  async listApps(): Promise<ArgusApp[]> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/apps', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    if (error) {
      this.logger?.error(`listing argus apps: ${JSON.stringify(error)}`);
      return [];
    }

    return data;
  }

  async listEnvs(appName: string): Promise<ArgusEnv[]> {
    const token = await this.tokenProvider.getToken();

    const { data, error } = await this.client.GET('/v1/deployments/findAppByName', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
        query: {
          appName,
        },
      },
    });
    if (error) {
      throw new Error(`finding argus app with name=${appName}: ${JSON.stringify(error)}`);
    }

    return data?.envs ?? [];
  }

  async listStacks(appName: string, envName: string): Promise<ArgusStack[]> {
    const envs = await this.listEnvs(appName);
    const env = envs.find((env) => env.name === envName);
    if (!env) {
      throw new Error(`env with name=${envName} not found`);
    }
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/envs/{id}/stacks', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
        path: {
          id: env.id,
        },
      },
    });
    if (error) {
      throw new Error(`listing argus stacks for app=${appName}/env=${envName}: ${JSON.stringify(error)}`);
    }

    return data;
  }

  async getStackInfo(appName: string, envName: string, stackName: string): Promise<ArgusStackInfo | undefined> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/getStackInfoByName', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
        query: {
          appName,
          envName,
          stackName,
        },
      },
    });
    if (error) {
      if (error.code === 404) {
        return undefined;
      }
      throw new Error(`fetching argus stack info for app=${appName}/env=${envName}/stack=${stackName}: ${JSON.stringify(error)}`);
    }
    return data;
  }

  async listClusters(): Promise<ArgusCluster[]> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/clusters', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    if (error) {
      this.logger?.error(`listing argus clusters: ${JSON.stringify(error)}`);
      return [];
    }

    return data;
  }

  async getStats(): Promise<ArgusStats> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/stats', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    if (error) {
      throw new Error(`fetching argus stats: ${JSON.stringify(error)}`);
    }
    return data;
  }

  async listEvents(query: EventQuery): Promise<{ events: ArgusEvent[], hasMore: boolean, totalEventCount: number }> {
    query.items_per_page = query.items_per_page ?? 100;
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/events', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
        query,
      },
    });
    if (error) {
      throw new Error(`listing argus events: ${JSON.stringify(error)}`);
    }
    return {
      events: data?.events ?? [],
      hasMore: data?.page_size === query.items_per_page,
      totalEventCount: data?.total_event_count ?? 0
    };
  }

  async listEventTypes(): Promise<string[]> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/events/types', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    if (error) {
      throw new Error(`listing argus event types: ${JSON.stringify(error)}`);
    }
    return data;
  }

  async listSourceSystems(): Promise<string[]> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/events/source-systems', {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    if (error) {
      throw new Error(`listing argus source systems: ${JSON.stringify(error)}`);
    }
    return data;
  }

  async listPermissions({appName, clusterName, envName, teamName}: {
    appName?: string;
    clusterName?: string;
    envName?: string;
    teamName?: string;
  }): Promise<paths['/v1/deployments/listPermissions']['get']['responses']['200']['content']['application/json']> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET(
      `/v1/deployments/listPermissions`,
      {
        params: {
          header: {
            Authorization: `Bearer ${token}`,
          },
          query: {
            appName,
            clusterName,
            envName,
            teamName,
          },
        },
      },
    );
    if (error) {
      throw new Error(`listing argus permissions: ${JSON.stringify(error)}`);
    }

    return data;
  }

  async reportDependencies(appName: string): Promise<ArgusStackDependency[]> {
    const token = await this.tokenProvider.getToken();
    const { data, error } = await this.client.GET('/v1/deployments/reportDependencies', {
      params: {
        header: { Authorization: `Bearer ${token}` },
        query: { appName, envName: '', stackName: '' },
      },
    });
    if (error) {
      this.logger?.error(`fetching dependencies for ${appName}: ${JSON.stringify(error)}`);
      return [];
    }
    return data;
  }

  async deleteStack(appName: string, envName: string, stackName: string): Promise<void> {
    const token = await this.tokenProvider.getToken();
    await this.client.DELETE(`/v1/deployments/deleteStackByName`, {
      params: {
        header: {
          Authorization: `Bearer ${token}`,
        },
        query: {
          appName,
          envName,
          stackName,
        },
      },
    });
  }
}

// Default path of the projected ServiceAccount token in any Kubernetes pod.
// The token at this path is signed by the cluster's OIDC issuer and carries
// the audience "https://kubernetes.default.svc", which Argus trusts for
// dev-central and prod-central. Kubelet refreshes the file before expiry.
export const DefaultServiceAccountTokenPath = '/var/run/secrets/kubernetes.io/serviceaccount/token';

export class ServiceAccountTokenProvider implements TokenProvider {
  private readonly token?: string;
  private readonly tokenPath: string;

  constructor(configApi: ConfigApi) {
    const config = configApi.getConfig('plugins.argus');
    // An inline token (ARGUS_SA_TOKEN) wins over the on-disk path. This is the
    // local-dev path: a laptop has no projected ServiceAccount token, so a token
    // minted from the argus-eng-portal-rdev namespace is pasted into .env. A
    // blank value is treated as unset so a stray "ARGUS_SA_TOKEN=" in .env does
    // not shadow the in-cluster token file.
    const inlineToken = config.getOptionalString('serviceAccountToken')?.trim();
    this.token = inlineToken === '' ? undefined : inlineToken;
    this.tokenPath = config.getOptionalString('serviceAccountTokenPath') ?? DefaultServiceAccountTokenPath;
  }

  async getToken(): Promise<string> {
    // When an inline token is configured, never touch the filesystem.
    if (this.token !== undefined) {
      return this.token;
    }
    const raw = await fs.readFile(this.tokenPath, 'utf8');
    const token = raw.trim();
    if (token === '') {
      throw new Error(`service account token at ${this.tokenPath} is empty`);
    }
    return token;
  }
}

export class OktaTokenProvider implements TokenProvider {
  private readonly config: ConfigApi;

  constructor(
    private readonly oktaTokenApi: OktaTokenApi,
    configApi: ConfigApi,
  ) {
    this.config = configApi.getConfig('plugins.argus');
  }

  async getToken(): Promise<string> {
    const clientId = this.config.getString('frontendOktaClientId');
    const issuerUrl = this.config.getOptionalString('frontendOktaIssuerUrl');
    const token = await this.oktaTokenApi.getToken(clientId, issuerUrl, ["openid", "profile", "groups"]);
    return token;
  }
}
